<template>
  <UserNotVerificated 
    v-if="!allowedMessageSend"/>

  <div v-else class="form">

    <div class="smile_btn">
      <div 
        @click="handleToggleEmoji"
        class="emoji_popup ui button icon"
      ><i class="icon-smile"></i>
      </div>

        
      <div class="emoji_cn" v-if="emojiActive" ref="elEmojiContent">
        <span
          v-for="(e,ek) in emoji"
          :key="ek"
          @click="handleInsertEmoji(e)"
          >{{ e }}</span>        
      </div> 
    </div>

 
    <div class="input">  
      <label for="chat_message_unput" class="hlabel">Message</label> 
      <textarea 
        placeholder="Your message..." 
        v-model="message"
        ref="elTextArea"
        @input="adjustHeight"
        @keydown.enter.exact.prevent="message += '\n'"
        @keydown.enter.shift.exact.prevent="send"
        ></textarea>
    </div>
    <div class="send_btn" @click="send">
      <div class="is_loading"></div>
      <i class="icon-message is_ready"></i>
    </div>

  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { onMounted, nextTick } from 'vue'

const { $api } = useNuxtApp();

const props = defineProps({
  room: {
    type: Object,
    required: true
  },
  allowedMessageSend: {
    type: Boolean,
    default: false
  }
});

const emits = defineEmits(['messageSent']);

const elTextArea = ref<HTMLTextAreaElement | null>(null);
const message = ref('');
const emojiActive = ref(false);
const emoji = ref(["😀","😬","😁","😂","😃","😄","😅","😆","😇","😉","😊","😋","😌","😍","😘","😗","😙","😚","😜","😝","😛","😎","😏","😶","😐","😑","😒","😳","😞","😟","😠","😡","😔","😕","😣","😖","😫","😩","😤","😮","😱","😨","😰","😯","😦","😧","😢","😥","😪","😓","😭","😵","😲","😷","😴","💤","💩","😈","👿","👹","👺","💀","👻","👽","😺","😸","😹","😻","😼","😽","🙀","😿","😾","🙌","👏","👋","👍","👊","✊","👌","✋","💪","🙏","👆","👇","👈","👉"])
const elEmojiContent = ref(null);

const handleInsertEmoji = (text: string) => {
  if (!elTextArea.value) return;
  
  const textarea = elTextArea.value;
  const scrollPos = textarea.scrollTop;
  let strPos = 0;

  // Получаем позицию курсора
  if (textarea.selectionStart !== null) {
    strPos = textarea.selectionStart;
  }

  // Вставляем emoji в позицию курсора
  const front = message.value.substring(0, strPos);
  const back = message.value.substring(strPos, message.value.length);
  message.value = front + text + back;
  
  // Устанавливаем курсор после вставленного emoji
  const newPos = strPos + text.length;
  
  nextTick(() => {
    if (elTextArea.value) {
      elTextArea.value.focus();
      elTextArea.value.setSelectionRange(newPos, newPos);
      elTextArea.value.scrollTop = scrollPos;
    }
  });

  handleToggleEmoji();
}

const handleToggleEmoji = () => {
  emojiActive.value = !emojiActive.value;
}

const adjustHeight = () => {
  if (!elTextArea.value || import.meta.server) return;
  
  const textarea = elTextArea.value;
  
  // Если нет контента, сбрасываем к минимальной высоте
  if (!message.value.trim()) {
    textarea.style.height = '40px';
    textarea.style.overflowY = 'hidden';
    return;
  }
  
  // Сбрасываем высоту для точного измерения
  textarea.style.height = '40px';
  
  // Получаем реальную высоту контента
  const scrollHeight = textarea.scrollHeight;
  
  // Максимум 5 строк (~120px), минимум 40px
  const maxHeight = 120; // примерно 5 строк
  const newHeight = Math.max(Math.min(scrollHeight, maxHeight), 40);
  
  // Включаем скролл если достигнут максимум
  textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  textarea.style.height = newHeight + 'px';
}

const save = function(){
  $api.postMessagesRoomMessage(props.room.id, message.value, props.room.address)
  .then((res) => {                               
    console.log(res);
  })
  .catch((err) => {
    console.log(err);
  });
}

const send = function(){
  if(!props.allowedMessageSend){
    useToast().error('You need to verify your email');
    return false;
  }

  if(!message.value) return false;

  const messageData = {
    id: 0,
    add_date: Date.now().toString().substr(0, 10),
    text: message.value,
    user_id: useAuth().getId(),
    guest_id: 0,
    user_name: useAuth().getName(),
    user_status: useAuth().getStatus(),
    user_visit: Date.now(),
    user_avatar: useAuth().getAvatarLittle(),
    is_my: 1,
    is_new: 1
  };

  emits('messageSent', messageData);
  save();
  message.value = '';
  
  // Принудительно сбрасываем высоту после отправки
  if (elTextArea.value) {
    elTextArea.value.style.height = '40px';
    elTextArea.value.style.overflowY = 'hidden';
  }
  
  nextTick(() => {
    adjustHeight();
  });       
}

// Устанавливаем начальную высоту при монтировании
onMounted(() => {
  nextTick(() => {
    adjustHeight();
  });
});

// Реагируем на изменения в message (например, при программной вставке emoji)
watch(message, () => {
  nextTick(() => {
    adjustHeight();
  });
});

onClickOutside(elEmojiContent, (event) => {
  handleToggleEmoji();
})
</script>

<style scoped>
/* FORM */
.form {
  width: 100%;
  min-height: 50px;
  bottom: 0px;
  display: flex;
  align-items: flex-end;
  /* position: sticky; */
  bottom: 0;
  background-color: var(--un-background-color-gray-light);
  z-index: 1001;
}

.form .smile_btn {
  display: inline-block;
  width: 46px;
  text-align: center;
  font-size: 1.4rem;
  padding: 5px 0px 4px 0px;
  vertical-align: middle;
  cursor: pointer;
  position: relative;
}

.form .emoji_popup {
  background-color: transparent;
  display: inline-block;
  padding: 0px;
  min-height: 43px;
  position: relative;
  margin-bottom: 0px !important;
  border: 0px;
  margin-top: 0px !important;
  line-height: 38px !important;
  vertical-align: bottom;
  font-size: 0.85re
}

.form .smile_btn .ui.popup {
  width: 100% !important;
}

.form .send_btn {
  display: inline-block;
  width: 46px;
  text-align: center;
  font-size: 1.4rem;
  padding: 5px 0px 12px 0px;
  vertical-align: middle;
  cursor: pointer;
  position: relative;
}

.form .send_btn .is_loading {
  display: none;
  position: absolute;
  content: '';
  top: -8px;
  left: 50%;
  margin: -0.64285714em 0em 0em -0.64285714em;
  width: 1.48571429em;
  height: 1.48571429em;
  border-radius: 500rem;
  background-image: url(/images/preloader.svg);
  background-repeat: no-repeat;
  background-size: 100%;
  z-index: 1;
}

.form .send_btn .is_ready {
  display: inline-block;
}

.form .send_btn.loading .is_loading {
  display: inline-block;
}

.form .send_btn.loading .is_ready {
  display: none;
}

.form .input {
  display: inline-block;
  width: calc(100% - 104px);
  position: relative;
}

.form .input textarea {
  padding: 8px 10px;
  border: 0px;
  width: 100%;
  min-height: 40px;
  height: 40px;
  resize: none;
  overflow-y: hidden;
  background-color: var(--un-background-color-gray-light);
  transition: height 0.1s ease;
  line-height: 1.2;
}

.emoji_cn{
  position: absolute;
  bottom: 50px;
  left: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
  padding: 5px;
  z-index: 1000;
  width: 330px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  min-width: 140px;
  color: var(--gd-text-color);
  overflow-y: auto;
  background: var(--un-background-color-gray-light);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  padding: 0.3rem 0.6rem;
}

.emoji_cn > span{
  cursor: pointer;
  transition: 300ms all ease;
}
.emoji_cn > span:hover{
  transform: scale(1.7);
}

@media (max-width: 768px) {
  .form .smile_btn {
    display: none !important;
  }

  .form .input {
    width: calc(100% - 64px);
    margin-left: 10px;
  }
}
</style>
